import { Dispatch, FC, SetStateAction, useState, ChangeEvent, useCallback } from 'react';
import cx from 'classnames';
import PopupContainer from 'shared/components/PopupContainer';
import ButtonDefault from 'shared/components/Buttons/ButtonsDefault';
import styles from './ReasonForRejectionPopup.module.scss';

type ReasonForRejectionPopupProps = {
	setIsShowPopup: Dispatch<SetStateAction<boolean>>;
	onConfirm: (reason: string) => void;
};

const MAX_LENGTH = 500;
const DISALLOWED_CHARS_REGEX = /[<>{}[\]%^&*;:"'|\\~]/;

const ReasonForRejectionPopup: FC<ReasonForRejectionPopupProps> = ({ setIsShowPopup, onConfirm }) => {
	const [reason, setReason] = useState<string>('');
	const [error, setError] = useState<string>('');

	const handleClose = useCallback(() => {
		if (typeof setIsShowPopup === 'function') {
			setIsShowPopup(false);
		}
		setReason('');
		setError('');
	}, [setIsShowPopup]);

	const handleReasonChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
		const value = e.target.value;

		// Update the value to allow typing
		setReason(value);

		// Check if value is only whitespaces
		if (value.trim().length === 0 && value.length > 0) {
			setError('Field cannot contain only whitespaces.');
			return;
		}

		// Check character limit
		if (value.length > MAX_LENGTH) {
			setError('Exceeded maximum length of 500 characters.');
			return;
		}

		// Check for disallowed characters
		if (DISALLOWED_CHARS_REGEX.test(value)) {
			setError('Unsupported characters found.');
			return;
		}

		// Clear error if validation passes
		setError('');
	}, []);

	const handleConfirm = useCallback(() => {
		// Check if value is only whitespaces
		if (reason.trim().length === 0 && reason.length > 0) {
			setError('Field cannot contain only whitespaces.');
			return;
		}

		// Validate one more time before confirming
		if (reason.length > MAX_LENGTH) {
			setError('Exceeded maximum length of 500 characters.');
			return;
		}

		if (DISALLOWED_CHARS_REGEX.test(reason)) {
			setError('Unsupported characters found.');
			return;
		}

		// Trim the reason before passing it to onConfirm (empty string is allowed since it's optional)
		onConfirm(reason.trim());
		handleClose();
	}, [reason, onConfirm, handleClose]);

	return (
		<PopupContainer
			setIsShowPopup={setIsShowPopup}
			isDisableCloseButton
			isFixedStyle
			isContentHeightAuto
			isContentCenter
			parentClassName={styles.container}
		>
			<div className={styles.popup}>
				<h2 className={styles.title}>Reason for rejection</h2>
				<div className={styles.content}>
					<div className={cx(styles.textareaWrapper, { [styles.hasError]: !!error })}>
						<textarea
							id="rejection-reason"
							className={styles.textarea}
							placeholder="(Optional) Please give a reason for rejecting this Opportunity"
							value={reason}
							onChange={handleReasonChange}
							rows={4}
						/>
						{error && <div className={styles.error}>{error}</div>}
					</div>
					<div className={styles.actions}>
						<ButtonDefault
							classList={['lg', 'secondary']}
							title="Cancel"
							clickHandler={handleClose}
							parentClassName={styles.cancelButton}
						/>
						<ButtonDefault
							classList={['lg', 'primary']}
							title="OK"
							disabled={!!error}
							clickHandler={handleConfirm}
							parentClassName={styles.okButton}
						/>
					</div>
				</div>
			</div>
		</PopupContainer>
	);
};

export default ReasonForRejectionPopup;
